import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { TableModule } from 'primeng/table';
import { IGlobalPricingItem } from '../../../_interfaces/ISuperadminCustomerAccount';
import { CustomersService } from '../../../_services/customers/customers.service';

@Component({
  selector: 'app-admin-global-pricing',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    TableModule,
    InputNumberModule,
    ButtonModule,
  ],
  templateUrl: './admin-global-pricing.component.html',
  styleUrl: './admin-global-pricing.component.scss',
})
export class AdminGlobalPricingComponent implements OnInit {
  loading = false;
  saving = false;
  currencyCode = 'EUR';
  rows: IGlobalPricingItem[] = [];
  loadFailed = false;

  constructor(
    private readonly customersService: CustomersService,
    private readonly messageService: MessageService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(showErrorToast = true) {
    this.loading = true;
    this.loadFailed = false;
    this.customersService.getGlobalPricingDefaults().subscribe({
      next: (response) => {
        this.currencyCode = response?.currency_code || 'EUR';
        const items = Array.isArray(response?.items) ? response.items : [];
        this.rows = items.map((item) => ({
          ...item,
          monthly_price: Number(item.monthly_price || 0),
        }));
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.loadFailed = true;
        if (showErrorToast) {
          this.toast('error', 'ADMIN.CUSTOMERS.PRICING_GLOBAL.MSG.ERROR');
        }
      },
    });
  }

  save() {
    if (this.saving) return;
    this.saving = true;

    this.customersService
      .updateGlobalPricingDefaults({
        items: (this.rows || []).map((row) => ({
          concept_type: row.concept_type,
          modules_id:
            row.concept_type === 'MODULE'
              ? row.modules_id || undefined
              : undefined,
          monthly_price: Number(row.monthly_price || 0),
        })),
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.toast('success', 'ADMIN.CUSTOMERS.PRICING_GLOBAL.MSG.SAVED');
          this.load(false);
        },
        error: () => {
          this.saving = false;
          this.toast('error', 'ADMIN.CUSTOMERS.PRICING_GLOBAL.MSG.ERROR');
        },
      });
  }

  getConceptLabel(row: IGlobalPricingItem): string {
    if (row.concept_type === 'STAFF_USER') {
      return this.translate.instant('ADMIN.CUSTOMERS.CONCEPT.STAFF_USER');
    }
    if (row.concept_type === 'SITE') {
      return this.translate.instant('ADMIN.CUSTOMERS.CONCEPT.SITE');
    }

    const identifier = `${row.module?.identifier || ''}`.trim();
    if (identifier) {
      const key = `ADMIN.CUSTOMERS.MODULE_LABELS.${identifier.toUpperCase()}`;
      const translated = `${this.translate.instant(key) || ''}`.trim();
      if (translated && translated !== key) {
        return translated;
      }
    }

    return (
      row.module?.name ||
      row.module?.identifier ||
      this.translate.instant('ADMIN.CUSTOMERS.CONCEPT.MODULE')
    );
  }

  private toast(severity: 'success' | 'error', detailKey: string) {
    this.messageService.add({
      severity,
      summary: this.translate.instant(
        severity === 'success' ? 'COMMON.SUCCESS' : 'COMMON.ERROR',
      ),
      detail: this.translate.instant(detailKey),
    });
  }
}
